import {FORMATS} from "./formatting";
import type {PickerVariable, VariableAttributes} from "./types";

export const DEFAULT_FALLBACK = "-";

/** The node type name: the document's half of the grammar, as `{{key:format|fallback}}` is the text's. */
export const VARIABLE_NODE_NAME = "variable";

/** One token as written: `format` and `fallback` are absent when the text does not name them. */
export interface VariableToken {
    key: string;
    format?: string;
    fallback?: string;
}

/*
    `{{key}}`, `{{key|fallback}}`, `{{key:format}}` or `{{key:format|fallback}}`, whitespace and all.
    A key holds no `{`, `}`, `|` or `:`; a format no `{`, `}` or `|`; a fallback anything but braces.
 */
const TOKEN_SOURCE = "\\{\\{\\s*(?<key>[^{}|:]+?)\\s*(?::\\s*(?<format>[^{}|]*?)\\s*)?(?:\\|\\s*(?<fallback>[^{}]*?)\\s*)?\\}\\}";

/**
 * A fresh global pattern on every call (groups `key`, `format`, `fallback`, also 1–3): a shared
 * `/g` regex carries `lastIndex` from one caller into the next.
 */
export const variableTokenPattern = () => new RegExp(TOKEN_SOURCE, "g");

/** The parts of exactly one token, or `null` when `text` is not one. */
export const parseVariableToken = (text: string): VariableToken | null => {
    const match = new RegExp(`^\\s*${TOKEN_SOURCE}\\s*$`).exec(text);
    if (!match) return null;

    const [, key, format, fallback] = match;
    return {
        key,
        ...(format !== undefined && {format}),
        ...(fallback !== undefined && {fallback}),
    };
};

/** A fallback the grammar can carry: braces would end the token early, so they are dropped. */
export const cleanFallback = (fallback: string) => fallback.replace(/[{}]/g, "");

/**
 * The token text for a chip. An empty format is left out; any string fallback is written, so an
 * author who cleared the default gets `{{key|}}` back — not the catalogue's default. Braces in the
 * fallback (from pasted HTML, say) are dropped, so the text always parses back into a chip.
 */
export const tokenFor = (key: string, {format, fallback}: {format?: string; fallback?: string} = {}) =>
    `{{${key}${format ? `:${format}` : ""}${typeof fallback === "string" ? `|${cleanFallback(fallback)}` : ""}}}`;

/**
 * The attributes a typed or pasted token becomes, or `false` when the key is not in the catalogue.
 * An absent fallback takes the catalogue's default, an explicit `""` stays empty; a format the
 * variable's kind does not know is dropped rather than stored for nobody to read.
 */
export const variableAttributes = (
    variables: readonly PickerVariable[],
    raw: string | Partial<VariableToken> | null | undefined,
): ({key: string} & VariableAttributes) | false => {
    const token = typeof raw === "string" ? parseVariableToken(raw) : raw;
    const key = token?.key?.trim();
    const variable = key ? variables.find(entry => entry.key === key) : undefined;
    if (!key || !variable) return false;

    const format = token?.format?.trim() ?? "";
    const fallback = token?.fallback;

    return {
        key,
        fallback: fallback === undefined ? variable.fallback ?? DEFAULT_FALLBACK : fallback.trim(),
        format: FORMATS[variable.kind]?.includes(format) ? format : "",
    };
};

/*
    What a chip closes up to: whitespace already separates it, and punctuation that ends a phrase or
    closes a bracket or quote sits right against it ("Frau Jung, wie…", "»Jung«"); a hyphen joins
    ("Jung-Team"). A word, another chip, an opening bracket or quote and the end of the text do not.
 */
const CLOSES_UP_TO = /^[\s.,;:!?…)\]}"'’‘”“»«›‹-]/u;

/**
 * What the picker inserts: the chip, then a space to carry on writing in — unless `following`, the
 * text right after the insertion point, starts with whitespace or closing punctuation.
 */
export const variableInsertContent = (variable: PickerVariable, following = "") => [
    {
        type: VARIABLE_NODE_NAME,
        attrs: {key: variable.key, fallback: variable.fallback ?? DEFAULT_FALLBACK, format: ""},
    },
    ...(CLOSES_UP_TO.test(following) ? [] : [{type: "text", text: " "}]),
];
